// Lançamentos recorrentes (provisões do mês) — cálculo puro, sem Supabase.
// Tabelas: contabil_recorrentes e contabil_recorrentes_gerados (migração 033).
//
// calcularRecorrentes devolve, para uma competência, o valor de cada
// recorrente e se ele deve ser gerado agora. A gravação fica no componente.

const cod = (conta) => String(conta || '').split(/ [—-] /)[0].trim();
const arred = (n) => Math.round(Number(n) * 100) / 100;

export function ultimoDiaDoMes(periodo) {
  const [a, m] = periodo.split('-').map(Number);
  const d = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return `${periodo}-${String(d).padStart(2, '0')}`;
}

// Receita líquida do mês: créditos menos débitos nas contas 4.1.x.
export function receitaDoMes(lancamentos, periodo) {
  return arred((lancamentos || [])
    .filter(l => (l.data || '').slice(0, 7) === periodo)
    .reduce((s, l) => {
      const v = Number(l.valor) || 0;
      if (cod(l.contaCredito).startsWith('4.1')) s += v;
      if (cod(l.contaDebito).startsWith('4.1')) s -= v;
      return s;
    }, 0));
}

export function historicoDaCompetencia(modelo, periodo) {
  const [a, m] = periodo.split('-');
  return String(modelo || '').replace(/\{competencia\}/g, `${m}/${a}`);
}

/**
 * @param {Array}  recorrentes  linhas de contabil_recorrentes da empresa
 * @param {Map}    gerados      recorrente_id → { valor } já gerados nesta competência
 * @param {string} periodo      'AAAA-MM'
 * @param {number} receita      receita líquida do mês
 * @returns {Array} [{ rec, valor, situacao: 'gerado'|'gerar'|'sem_valor'|'fora', motivo }]
 */
export function calcularRecorrentes(recorrentes, gerados, periodo, receita) {
  const ordenados = [...(recorrentes || [])].sort((a, b) => (a.ordem - b.ordem) || String(a.nome).localeCompare(String(b.nome), 'pt-BR'));
  const valores = new Map();
  const resultado = [];

  // Bases podem vir depois na ordem: resolve por dependência, com trava contra ciclo.
  const resolver = (rec, pilha = new Set()) => {
    if (valores.has(rec.id)) return valores.get(rec.id);
    const jaGerado = gerados.get(rec.id);
    if (jaGerado) { valores.set(rec.id, Number(jaGerado.valor) || 0); return valores.get(rec.id); }
    if (pilha.has(rec.id)) return 0;
    pilha.add(rec.id);
    const porId = (id) => ordenados.find(r => r.id === id);
    let v = 0;
    if (rec.calculo === 'fixo') v = Number(rec.valor) || 0;
    else if (rec.calculo === 'percentual_receita') v = receita * (Number(rec.percentual) || 0) / 100;
    else if (rec.calculo === 'percentual_de') {
      const base = porId(rec.base_id);
      v = base ? resolver(base, pilha) * (Number(rec.percentual) || 0) / 100 : 0;
    } else if (rec.calculo === 'restante_de') {
      const base = porId(rec.base_id);
      v = base ? resolver(base, pilha) : 0;
      (rec.deduz_ids || []).forEach(id => { const d = porId(id); if (d) v -= resolver(d, pilha); });
    }
    valores.set(rec.id, arred(v));
    return valores.get(rec.id);
  };

  ordenados.forEach(rec => {
    const valor = resolver(rec);
    let situacao = 'gerar';
    let motivo = '';
    if (gerados.has(rec.id)) situacao = 'gerado';
    else if (!rec.ativo) { situacao = 'fora'; motivo = 'Inativo'; }
    else if (periodo < rec.inicio) { situacao = 'fora'; motivo = `Começa em ${rec.inicio.slice(5)}/${rec.inicio.slice(0, 4)}`; }
    else if (!(valor > 0)) {
      situacao = 'sem_valor';
      motivo = rec.calculo === 'percentual_receita' ? 'Sem receita lançada no mês' : 'Valor zerado';
    }
    resultado.push({ rec, valor, situacao, motivo });
  });
  return resultado;
}

export function paraLancamento(item, { empresaSlug, periodo }) {
  const { rec, valor } = item;
  return {
    empresa_slug: empresaSlug,
    data: ultimoDiaDoMes(periodo),
    historico: historicoDaCompetencia(rec.historico, periodo),
    conta_debito: rec.conta_debito,
    conta_credito: rec.conta_credito,
    valor: arred(valor),
    origem: 'recorrente',
    chave_dedup: `rec:${rec.id}:${periodo}`,
  };
}
